/**
 * Growth Adjustment Card for Accountant Report View
 * Displays the 4-step growth adjustment calculation
 */

import React from 'react';
import CalculationMethodology from '../CalculationMethodology';

interface GrowthAdjustment {
  claimant_factor_pct?: number;
  general_factor_pct?: number;
  total_factor_pct?: number;
  incremental_lost_profit?: number;
  incremental_revenue?: number;
}

interface GrowthAdjustmentCardProps {
  growthAdjustment: GrowthAdjustment;
  benchmarkRevenue?: number;
  variableMarginPct?: number;
  financialData?: any;
  formatCurrency: (amount?: number) => string;
  formatPercent: (value?: number) => string;
}

export default function GrowthAdjustmentCard({
  growthAdjustment,
  benchmarkRevenue,
  variableMarginPct,
  financialData,
  formatCurrency,
  formatPercent
}: GrowthAdjustmentCardProps) {
  if (!growthAdjustment) return null;

  // Calculate historical revenues for methodology explanation
  const historicalRevenues = financialData?.yearly_data ? 
    Object.entries(financialData.yearly_data)
      .sort(([yearA], [yearB]) => parseInt(yearA) - parseInt(yearB))
      .map(([year, months]: [string, any], idx, arr) => {
        const yearRevenue = months.reduce((sum: number, m: any) => sum + (m.revenue || 0), 0);
        const prevYearRevenue = idx > 0 ? (arr[idx - 1][1] as any[]).reduce((sum: number, m: any) => sum + (m.revenue || 0), 0) : null;
        const changePct = prevYearRevenue ? ((yearRevenue - prevYearRevenue) / prevYearRevenue) * 100 : null;
        return {
          year: parseInt(year),
          revenue: yearRevenue,
          change_pct: changePct
        };
      })
    : [];

  return (
    <div className="rounded-xl shadow-sm border p-6 mb-6" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
      <h2 className="text-xl font-bold mb-4 flex items-center" style={{ color: 'var(--color-text-primary)' }}>
        Growth Adjustment (Step 2)
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Step 1: Claimant Factor */}
        <div className="rounded-lg p-4 border-l-4" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-text-dim)' }}>
          <p className="text-xs font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>STEP 1</p>
          <p className="text-sm mb-2 font-semibold" style={{ color: 'var(--color-text-secondary)' }}>Claimant Factor</p>
          <p className="text-2xl font-bold mb-2" style={{ color: 'var(--color-text-primary)' }}>
            {formatPercent(growthAdjustment.claimant_factor_pct)}
          </p>
          <div className="text-xs rounded px-2 py-1" style={{ color: 'var(--color-text-secondary)', backgroundColor: 'var(--color-bg-hover)' }}>
            Historical trend regression
          </div>
        </div>

        {/* Step 2: General Factor */}
        <div className="rounded-lg p-4 border-l-4" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-text-dim)' }}>
          <p className="text-xs font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>STEP 2</p>
          <p className="text-sm mb-2 font-semibold" style={{ color: 'var(--color-text-secondary)' }}>General Factor</p>
          <p className="text-2xl font-bold mb-2" style={{ color: 'var(--color-text-primary)' }}>
            {formatPercent(growthAdjustment.general_factor_pct)}
          </p>
          <div className="text-xs rounded px-2 py-1" style={{ color: 'var(--color-text-secondary)', backgroundColor: 'var(--color-bg-hover)' }}>
            Industry benchmark
          </div>
        </div>

        {/* Step 3: Total Factor */}
        <div className="rounded-lg p-4 border-l-4" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-brand)' }}>
          <p className="text-xs font-medium mb-1" style={{ color: 'var(--color-needs-human-text)' }}>STEP 3: SUM</p>
          <p className="text-sm mb-2 font-semibold" style={{ color: 'var(--color-needs-human-text)' }}>Total Factor</p>
          <p className="text-2xl font-bold mb-2" style={{ color: 'var(--color-needs-human-text)' }}>
            {formatPercent(growthAdjustment.total_factor_pct)}
          </p>
          <div className="text-xs rounded px-2 py-1 font-mono" style={{ color: 'var(--color-needs-human-text)', backgroundColor: 'var(--color-needs-human-tint)' }}>
            {formatPercent(growthAdjustment.claimant_factor_pct)} + {formatPercent(growthAdjustment.general_factor_pct)}
          </div>
        </div>

        {/* Step 4: Incremental Profit */}
        <div className="rounded-lg p-4 border-l-4" style={{ backgroundColor: 'transparent', borderColor: 'var(--color-verified)' }}>
          <p className="text-xs font-medium mb-1" style={{ color: 'var(--color-verified)' }}>STEP 4: RESULT</p>
          <p className="text-sm mb-2 font-semibold" style={{ color: 'var(--color-verified)' }}>Incremental Profit</p>
          <p className="text-2xl font-bold mb-2" style={{ color: 'var(--color-verified)' }}>
            {formatCurrency(growthAdjustment.incremental_lost_profit)}
          </p>
          <div className="text-xs rounded px-2 py-1 font-mono" style={{ color: 'var(--color-verified)', backgroundColor: 'transparent' }}>
            ({formatCurrency(benchmarkRevenue)} × {formatPercent(growthAdjustment.total_factor_pct)}) × {formatPercent(variableMarginPct)}
          </div>
        </div>
      </div>
      
      {/* Methodology Explanation */}
      <CalculationMethodology 
        section="growth_adjustment" 
        data={{
          ...growthAdjustment,
          benchmark_revenue: benchmarkRevenue,
          variable_margin_pct: variableMarginPct,
          but_for_revenue: undefined,
          historical_revenues: historicalRevenues
        }} 
      />
    </div>
  );
}
